/**
 * TileEffects -- Manages persistent tile effects on the combat grid.
 *
 * Fire, ice, poison cloud, and trap effects that persist for multiple turns.
 * Units stepping onto affected tiles or starting their turn on one
 * receive the associated effect.
 *
 * @module TileEffects
 */

import eventBus from '../core/EventBus.js';

/**
 * Tile effect type definitions.
 * Each type has a color for visual rendering, damage, and status to apply.
 */
const TILE_EFFECT_TYPES = {
  fire: {
    name: 'Fire',
    color: 0xff4422,
    alpha: 0.35,
    damagePerTurn: 5,
    damageType: 'fire',
    statusId: 'burning',
    statusChance: 0.3,
    statusDuration: 2,
    cssClass: 'tile-effect--fire',
  },
  ice: {
    name: 'Ice Patch',
    color: 0x44aaff,
    alpha: 0.3,
    damagePerTurn: 0,
    damageType: 'frost',
    statusId: 'slowed',
    statusChance: 0.6,
    statusDuration: 2,
    cssClass: 'tile-effect--ice',
  },
  poison: {
    name: 'Poison Cloud',
    color: 0x44cc44,
    alpha: 0.3,
    damagePerTurn: 3,
    damageType: 'poison',
    statusId: 'poisoned',
    statusChance: 0.5,
    statusDuration: 3,
    cssClass: 'tile-effect--poison',
  },
  trap: {
    name: 'Trap',
    color: 0xccaa22,
    alpha: 0.25,
    damagePerTurn: 8,
    damageType: 'physical',
    statusId: 'rooted',
    statusChance: 0.8,
    statusDuration: 1,
    cssClass: 'tile-effect--trap',
  },
};

class TileEffectsSystem {
  constructor() {
    /**
     * Active tile effects: Map<"x,y", TileEffect[]>
     * @type {Map<string, object[]>}
     */
    this._effects = new Map();

    /**
     * Reference to the CombatSystem.
     * @type {object|null}
     */
    this._combatSystem = null;

    /**
     * Reference to the StatusEffects system.
     * @type {object|null}
     */
    this._statusEffects = null;
  }

  /**
   * Initialize with references to other systems.
   * @param {object} combatSystem
   * @param {object} [statusEffects]
   */
  init(combatSystem, statusEffects = null) {
    this._combatSystem = combatSystem;
    this._statusEffects = statusEffects;
  }

  /**
   * Reset all tile effects (call at combat start).
   */
  reset() {
    this._effects.clear();
  }

  /**
   * Add a persistent tile effect at a position.
   * @param {number} x - Grid column
   * @param {number} y - Grid row
   * @param {string} type - Effect type: 'fire', 'ice', 'poison', 'trap'
   * @param {number} duration - Turns before the effect expires
   * @param {object} [source] - The combatant who created this effect
   */
  addEffect(x, y, type, duration, source = null) {
    const typeDef = TILE_EFFECT_TYPES[type];
    if (!typeDef) return;

    const key = `${x},${y}`;
    if (!this._effects.has(key)) {
      this._effects.set(key, []);
    }

    // Don't stack same type on same tile; refresh duration instead
    const existing = this._effects.get(key).find(e => e.type === type);
    if (existing) {
      existing.duration = Math.max(existing.duration, duration);
      return;
    }

    const effect = {
      x,
      y,
      type,
      name: typeDef.name,
      duration,
      source: source ? source.id : null,
      color: typeDef.color,
      alpha: typeDef.alpha,
    };

    this._effects.get(key).push(effect);

    this._log(`${typeDef.name} appears at (${x}, ${y})!`);

    eventBus.emit('combat:tile-effect-add', { x, y, type, duration });
  }

  /**
   * Remove a tile effect.
   * @param {number} x
   * @param {number} y
   * @param {string} type
   */
  removeEffect(x, y, type) {
    const key = `${x},${y}`;
    const list = this._effects.get(key);
    if (!list) return;

    const idx = list.findIndex(e => e.type === type);
    if (idx !== -1) {
      list.splice(idx, 1);
      if (list.length === 0) {
        this._effects.delete(key);
      }
    }
  }

  /**
   * Get all tile effects at a position.
   * @param {number} x
   * @param {number} y
   * @returns {object[]}
   */
  getEffectsAt(x, y) {
    const key = `${x},${y}`;
    return this._effects.get(key) || [];
  }

  /**
   * Get all active tile effects across the grid.
   * @returns {object[]} Flat array of all tile effects
   */
  getAllEffects() {
    const all = [];
    for (const list of this._effects.values()) {
      all.push(...list);
    }
    return all;
  }

  /**
   * Check if a tile has any effects.
   * @param {number} x
   * @param {number} y
   * @returns {boolean}
   */
  hasTileEffect(x, y) {
    const key = `${x},${y}`;
    const list = this._effects.get(key);
    return list !== undefined && list.length > 0;
  }

  /**
   * Apply tile effects when a unit steps onto or starts turn on a tile.
   * @param {object} combatant
   */
  applyEffectsToUnit(combatant) {
    if (!combatant.alive) return;

    const effects = this.getEffectsAt(combatant.gridX, combatant.gridY);
    for (const effect of effects) {
      const typeDef = TILE_EFFECT_TYPES[effect.type];
      if (!typeDef) continue;

      // Apply damage
      if (typeDef.damagePerTurn > 0) {
        combatant.hp -= typeDef.damagePerTurn;
        combatant.hp = Math.max(0, combatant.hp);
        this._log(`${combatant.name} takes ${typeDef.damagePerTurn} ${typeDef.damageType} damage from ${typeDef.name}!`);

        if (combatant.hp <= 0) {
          combatant.alive = false;
          this._log(`${combatant.name} has been defeated by ${typeDef.name}!`);
          eventBus.emit('combat:kill', { attacker: null, target: combatant });
          return;
        }
      }

      // Apply status effect
      if (typeDef.statusId && Math.random() < typeDef.statusChance) {
        if (this._statusEffects) {
          this._statusEffects.applyStatus(
            combatant, typeDef.statusId, typeDef.statusDuration, null
          );
        } else {
          // Fallback
          const existing = combatant.statusEffects.find(e => e.id === typeDef.statusId);
          if (!existing) {
            combatant.statusEffects.push({
              id: typeDef.statusId,
              name: typeDef.statusId,
              duration: typeDef.statusDuration,
              effects: [],
            });
            this._log(`${combatant.name} is now ${typeDef.statusId} from ${typeDef.name}!`);
          }
        }
      }

      // Traps are consumed on trigger
      if (effect.type === 'trap') {
        this.removeEffect(combatant.gridX, combatant.gridY, 'trap');
      }
    }
  }

  /**
   * Tick all tile effects at the end of a round.
   * Decrements durations and removes expired effects.
   */
  tickEffects() {
    const toRemove = [];

    for (const [key, list] of this._effects) {
      for (let i = list.length - 1; i >= 0; i--) {
        const effect = list[i];
        if (effect.duration > 0) {
          effect.duration--;
          if (effect.duration <= 0) {
            const typeDef = TILE_EFFECT_TYPES[effect.type];
            this._log(`${typeDef ? typeDef.name : effect.type} at (${effect.x}, ${effect.y}) fades away.`);
            list.splice(i, 1);

            eventBus.emit('combat:tile-effect-expire', {
              x: effect.x,
              y: effect.y,
              type: effect.type,
            });
          }
        }
      }

      if (list.length === 0) {
        toRemove.push(key);
      }
    }

    for (const key of toRemove) {
      this._effects.delete(key);
    }
  }

  /**
   * Log a message via CombatSystem if available.
   * @param {string} msg
   */
  _log(msg) {
    if (this._combatSystem) {
      this._combatSystem._addLog(msg);
    }
  }
}

/** Singleton instance. */
const tileEffectsSystem = new TileEffectsSystem();
export default tileEffectsSystem;
export { TileEffectsSystem, TILE_EFFECT_TYPES };
